import React, { useState, useRef } from 'react';
import { API } from '../../services/api';
import './MediaUploader.css';

function MediaUploader({ onUploadComplete, onError }) {
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [category, setCategory] = useState('other');
    const [altText, setAltText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileInputChange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleFileSelect = (file) => {
        // Validation du type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            onError('Type de fichier non autorisé. Formats acceptés: JPG, PNG, GIF, SVG, WebP');
            return;
        }

        // Validation de la taille (5 MB max)
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            onError('Fichier trop volumineux. Taille maximale: 5 MB');
            return;
        }

        // Stocker le fichier et créer un preview
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        // Upload
        setUploading(true);
        setProgress(0);

        try {
            // Simuler la progression
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 100);

            const response = await API.uploadMedia(selectedFile, {
                alt_text: altText,
                category: category
            });

            clearInterval(progressInterval);
            setProgress(100);

            // Réinitialiser le formulaire
            setAltText('');
            setCategory('other');
            setSelectedFile(null);
            setPreviewUrl(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Notifier le parent
            if (onUploadComplete && response.data && response.data.media) {
                onUploadComplete(response.data.media);
            }

            setTimeout(() => {
                setUploading(false);
                setProgress(0);
            }, 500);

        } catch (error) {
            console.error('Upload error:', error);
            onError(error.message || 'Erreur lors de l\'upload');
            setUploading(false);
            setProgress(0);
        }
    };

    return (
        <div className="media-uploader">
            {!selectedFile ? (
                <div
                    className={`upload-dropzone ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                    />

                    <svg
                        width="48"
                        height="48"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                    </svg>
                    <h3>Glissez une image ici</h3>
                    <p>ou cliquez pour parcourir</p>
                    <span className="upload-hint">JPG, PNG, GIF, SVG, WebP (max. 5 MB)</span>
                </div>
            ) : uploading ? (
                <div className="upload-dropzone uploading">
                    <div className="upload-progress">
                        <div className="spinner-upload"></div>
                        <p>Upload en cours...</p>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>
                        <span className="progress-text">{progress}%</span>
                    </div>
                </div>
            ) : (
                <div className="upload-preview">
                    <div className="preview-image-container">
                        <img src={previewUrl} alt="Preview" className="preview-image" />
                        <button
                            className="preview-remove"
                            onClick={() => {
                                setSelectedFile(null);
                                setPreviewUrl(null);
                                if (fileInputRef.current) {
                                    fileInputRef.current.value = '';
                                }
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    <div className="preview-filename">{selectedFile?.name}</div>
                </div>
            )}

            {/* Options d'upload */}
            {selectedFile && !uploading && (
                <>
                    <div className="upload-options">
                        <div className="upload-option">
                            <label>Catégorie</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                <option value="other">Autre</option>
                                <option value="logo">Logo</option>
                                <option value="photo">Photo</option>
                                <option value="icon">Icône</option>
                                <option value="banner">Bannière</option>
                            </select>
                        </div>

                        <div className="upload-option">
                            <label>Texte alternatif (optionnel)</label>
                            <input
                                type="text"
                                value={altText}
                                onChange={(e) => setAltText(e.target.value)}
                                placeholder="Description de l'image"
                            />
                        </div>
                    </div>

                    <div className="upload-actions">
                        <button
                            className="btn-upload"
                            onClick={handleUpload}
                        >
                            Uploader
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default MediaUploader;
